import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ticketsAPI } from '../services/api';
import { formatDateTimeEST } from '../utils/dateUtils';

interface TicketData {
  ticketNumber: string;
  ticketType: string;
  customerName: string;
  email: string;
  eventTitle: string;
  eventAddress: string;
  eventStartDate: string;
  eventEndDate: string;
  scanned: boolean;
  scannedAt: string | null;
  generatedAt: string;
}

const ValidateTicket: React.FC = () => {
  const { ticketUuid } = useParams<{ ticketUuid: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [justScanned, setJustScanned] = useState(false);
  const [ticket, setTicket] = useState<TicketData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validateTicket = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ticketsAPI.validate(ticketUuid!);
      setValid(response.data.valid);
      setJustScanned(response.data.justScanned || false);
      setTicket(response.data.ticket);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to validate ticket');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (ticketUuid) {
      validateTicket();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketUuid]);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const formatDate = (dateString: string) => {
    return formatDateTimeEST(dateString);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600 mx-auto mb-4"></div>
          <p className="text-primary-700 text-xl font-semibold">Validating ticket...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-50 to-primary-100">
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
          {error ? (
            <div className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-red-100 mx-auto mb-6 flex items-center justify-center">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Ticket Not Found</h1>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition"
              >
                Go to Home
              </button>
            </div>
          ) : ticket ? (
            <>
              {/* Status Header */}
              <div className={`p-6 text-center ${valid ? 'bg-green-500' : 'bg-red-500'}`}>
                <div className="w-20 h-20 rounded-full bg-white mx-auto mb-4 flex items-center justify-center">
                  {valid ? (
                    <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  )}
                </div>
                <h1 className="text-3xl font-bold text-white mb-2">
                  {valid ? (justScanned ? 'Entry Granted! ✓' : 'Valid Ticket ✓') : 'Invalid Ticket - Already Used'}
                </h1>
                <p className="text-white text-lg opacity-90">
                  {valid ? (justScanned ? 'Ticket scanned successfully - Entry allowed' : 'This ticket is valid') : 'This ticket has already been scanned'}
                </p>
              </div>

              {/* Ticket Details */}
              <div className="p-8">
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Ticket Number</p>
                    <p className="text-lg font-bold text-gray-900">{ticket.ticketNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Ticket Type</p>
                    <p className="text-lg font-semibold text-gray-900 capitalize">{ticket.ticketType}</p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg p-6 mb-6">
                  <h2 className="text-2xl font-bold text-primary-900 mb-4">{ticket.eventTitle}</h2>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-primary-600 mt-1 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <p className="text-primary-800">{ticket.eventAddress}</p>
                    </div>
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-primary-600 mt-1 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <div>
                        <p className="text-primary-800"><strong>Start:</strong> {formatDate(ticket.eventStartDate)}</p>
                        <p className="text-primary-800"><strong>End:</strong> {formatDate(ticket.eventEndDate)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Customer Information</h3>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-gray-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <p className="text-gray-800">{ticket.customerName}</p>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-gray-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-800">{ticket.email}</p>
                    </div>
                  </div>
                </div>

                {ticket.scanned && ticket.scannedAt && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                    <div className="flex">
                      <svg className="w-5 h-5 text-yellow-400 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <h4 className="text-sm font-semibold text-yellow-800">Already Scanned</h4>
                        <p className="text-sm text-yellow-700 mt-1">Scanned at: {formatDate(ticket.scannedAt)}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Button */}
                <div className="flex justify-center">
                  <button
                    onClick={() => navigate('/')}
                    className="px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition shadow-lg"
                  >
                    Back to Home
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </main>
    </div>
  );
};

export default ValidateTicket;
